import React, { useMemo } from "react";
import { useReactFlow } from "reactflow";
import {
  getId,
  generateDroppedNodes,
  findFirstMissingNumber,
  removeDuplicateGames,
} from "./helper";
import {
  useTeamDataStore,
  useGameNumberStore,
  useDroppedNodeStore,
  useDuplicateGameStore,
} from "../../../utilities/stores";

const utils = () => {
  const { getNode, getNodes, getEdges, setNodes, setEdges, addNodes } =
    useReactFlow();
  const { setDroppedNodes } = useDroppedNodeStore();
  const { duplicateGames, setDuplicateGames } = useDuplicateGameStore();
  const { gameNumbers } = useGameNumberStore();
  const { teamData } = useTeamDataStore();

  //* Generate the game number dropdown
  const gameLists = useMemo(() => {
    return gameNumbers.map((value, index) => (
      <option key={index} value={value}>
        {value}
      </option>
    ));
  }, [gameNumbers]);

  //* Get Labels
  const labels = useMemo(() => {
    return teamData.reduce((acc, item) => {
      acc[item.key] = item.label.replace(/\s+/g, " ").trim();
      return acc;
    }, {});
  }, [teamData]);

  //* Update only node not the data
  const updateNode = (id, dataToUpdate) => {
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === id ? { ...node, ...dataToUpdate } : node
      )
    );
  };

  //* Update Nodes Data Key
  const updateNodeData = (id, dataToUpdate) => {
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === id
          ? { ...node, data: { ...node.data, ...dataToUpdate } }
          : node
      )
    );
  };

  //* Reload/Update the dropped node's store for active the sidebar
  const updateUsedTitles = async (nodes = [], isData = false) => {
    const nodesData = nodes.length ? nodes : await getNodes();
    const droppedNodes = await generateDroppedNodes(nodesData, isData);
    await setDroppedNodes(droppedNodes);
  };

  //* Reload/Update duplicate games while reset,save and load
  const updateDuplicateGames = async () => {
    const nodes = await getNodes();
    const seenGames = new Set();
    const duplicateGames = new Set();

    nodes.forEach((item) => {
      if (item.data && item.data.game_number !== undefined) {
        const gameNumber = String(item.data.game_number);
        if (seenGames.has(gameNumber)) {
          duplicateGames.add(gameNumber);
        } else {
          seenGames.add(gameNumber);
        }
      }
    });

    //* Filter out the IDs corresponding to duplicate game numbers
    const duplicateIds = nodes
      .filter((item) => duplicateGames.has(String(item.data.game_number)))
      .reduce((acc, item) => {
        acc[item.id] = String(item.data.game_number);
        return acc;
      }, {});

    setDuplicateGames(duplicateIds);
  };

  //* Change the winner when the connected game number has been changed
  const updateWinner = async (oldGame, newGame, nodeId) => {
    const allEdges = getEdges();
    const edge = allEdges.find((e) => e.source == nodeId);

    if (edge) {
      const node = getNode(edge.target);
      let newData = { ...node.data, newGenerated: true };
      if (
        newData.team_1_of_type == "winner" &&
        newData.team_1_of_type_value == oldGame
      ) {
        newData = {
          ...newData,
          team_1_of_type_value: newGame,
          team_1_of_type_label: `${labels["winner"]} ${newGame}`,
        };
      } else if (
        newData.team_2_of_type == "winner" &&
        newData.team_2_of_type_value == oldGame
      ) {
        newData = {
          ...newData,
          team_2_of_type_value: newGame,
          team_2_of_type_label: `${labels["winner"]} ${newGame}`,
        };
      }

      updateNodeData(node.id, newData);
    }
  };

  //* Update What if node
  const updateWhatIfNode = async (newGame, nodeId) => {
    const allEdges = getEdges();
    const edge = allEdges.find(
      (e) => e.source == nodeId && e.type == "dottedSmoothStepEdge"
    );

    if (edge) {
      const node = getNode(edge.target);
      let newData = {
        ...node.data,
        team_1_of_type_value: newGame,
        team_1_of_type_label: `${labels[node.data.team_1_of_type]} ${newGame}`,
        team_2_of_type_value: newGame,
        team_2_of_type_label: `${labels[node.data.team_2_of_type]} ${newGame}`,
        newGenerated: true,
      };

      updateNodeData(node.id, newData);
    }
  };

  /**
   * Menu Functions
   */
  //* Delete Single Node
  const deleteSingleNode = async (id) => {
    let nodesId = [id];
    const whatIfNode = await deleteWhatIfNode(id);
    if (whatIfNode) nodesId = [...nodesId, whatIfNode];

    const currentNode = getNode(id);

    //* Clear the winner/loser title
    clearWinnerLoser([currentNode.data.game_number], nodesId);

    setEdges((edges) =>
      edges
        .filter((edge) => edge.source != id)
        .filter((edge) => edge.target != id)
    );

    //* Remove if the single game number are available.
    delete duplicateGames[id];
    const removedGames = await removeDuplicateGames(duplicateGames);
    await setDuplicateGames(removedGames);
  };

  //* Delete Multiple Node
  const deleteMultipleNode = async ({ nodes = [], edges = [] }) => {
    if (nodes.length) {
      let selectedNodesId = nodes.map((node) => node.id);
      const whatIfNodes = await deleteWhatIfNode(selectedNodesId, true);

      if (whatIfNodes) selectedNodesId = [...selectedNodesId, ...whatIfNodes];

      const selectedGames = [
        ...new Set(
          nodes
            .filter(
              (item) => item.type !== "titleNode" && item.data.game_number
            )
            .map((item) => item.data.game_number) || []
        ),
      ];

      //* Clear the winner/loser title
      clearWinnerLoser(selectedGames, selectedNodesId);
    }

    if (edges.length) {
      const selectedEdgesId = edges.map((edge) => edge.id);
      setEdges((edges) =>
        edges.filter((edge) => !selectedEdgesId.includes(edge.id))
      );
    }
  };

  //* Duplicate single node
  const duplicateSingleNode = (id) => {
    const node = getNode(id);

    if (node) {
      const position = {
        x: node.position.x + 50,
        y: node.position.y + 50,
      };
      let data = { ...node.data };

      Object.keys(data).forEach((key) => {
        if (key != "hasWorkingTeam") {
          if (key.includes("cancelled")) {
            data[key] = true;
          } else {
            data[key] = key.includes("score") ? "00" : null;
          }
        }
      });

      if (node.type === "defaultNode" || node.type === "compassNode") {
        const { newNumber } = findFirstMissingNumber(gameNumbers);
        data = { ...data, game_number: newNumber };

        delete node.bracket_matchup_id;
      } else if (node.type == "titleNode") {
        delete node.title_id;
      }

      updateNode(id, { selected: false });
      addNodes({
        ...node,
        id: `${node.id}-copy-${getId()}`,
        position,
        data,
        selected: true,
      });
    }
  };

  //* Duplicate multiple node
  const duplicateMultipleNode = (selectedData) => {
    const newID = getId();
    const { newNumber } = findFirstMissingNumber(gameNumbers);
    const arrWinnerLoser = ["winner", "loser"];
    const propertiesToCheck = ["team_1_of_type", "team_2_of_type"];
    const arrTypeValue = [
      ...propertiesToCheck,
      "team_1_of_type_value",
      "team_2_of_type_value",
    ];

    let winnerLoser = {};

    let newGameNumber = 0;

    const newNodes = selectedData.nodes
      .sort((a, b) => a.data.game_number - b.data.game_number)
      .map((item) => {
        const { bracket_matchup_id, title_id, data, ...rest } = item;
        let newData = { ...data };

        // Reset values not in arrTypeValue
        Object.keys(newData).forEach((key) => {
          if (!arrTypeValue.includes(key) && key != "hasWorkingTeam") {
            if (key.includes("cancelled")) {
              newData[key] = true;
            } else {
              newData[key] = key.includes("score") ? "00" : "";
            }
          }
        });

        if (item.type !== "titleNode") {
          newData["game_number"] = newNumber + newGameNumber;
          winnerLoser[data.game_number] = newNumber + newGameNumber;
          newGameNumber++;
        }

        // Update winner/loser values and reset values not in arrWinnerLoser
        propertiesToCheck.forEach((key) => {
          if (arrWinnerLoser.includes(newData[key])) {
            const valueKey = `${key}_value`;

            if (winnerLoser[data[valueKey]]) {
              newData[valueKey] = winnerLoser[data[valueKey]];
              newData[`${key}_label`] = `${labels[newData[key]]} ${
                winnerLoser[data[valueKey]]
              }`;
            }
          } else {
            newData[key] = "";
            newData[`${key}_value`] = "";
          }
        });

        return {
          ...rest,
          data: newData,
          id: `${item.id}-copy-${newID}`,
          position: {
            x: item.position.x + 50,
            y: item.position.y + 50,
          },
        };
      });

    const newEdges = selectedData.edges.map((edge) => ({
      ...edge,
      id: `${edge.id}-copy-${newID}`,
      source: `${edge.source}-copy-${newID}`,
      target: `${edge.target}-copy-${newID}`,
    }));

    setNodes((nodes) => [
      ...nodes.map((node) => ({ ...node, selected: false })),
      ...newNodes,
    ]);
    setEdges((edges) => [
      ...edges.map((edge) => ({ ...edge, selected: false })),
      ...newEdges,
    ]);
  };
  /** End */

  //* Delete whatifNode
  const deleteWhatIfNode = (id, multiple = false) => {
    const edges = getEdges().filter(
      (edge) =>
        (multiple ? id.includes(edge.source) : edge.source === id) &&
        edge.type === "dottedSmoothStepEdge"
    );

    return multiple
      ? edges.map((edge) => edge.target)
      : (edges[0] && edges[0].target) || false;
  };

  //* Unselect the nodes
  const unSelectNodes = () => {
    setNodes((nodes) => nodes.map((node) => ({ ...node, selected: false })));
  };

  //* Delete node to clear the winner and loser
  const clearWinnerLoser = (selectedGames = [], selectedNodesId = []) => {
    const teamTypes = ["winner", "loser"];

    if (selectedGames.length > 0) {
      setNodes((nodes) =>
        nodes
          .filter((node) => !selectedNodesId.includes(node.id))
          .map((node) => {
            const nodeData = { ...node.data, newGenerated: true };

            const updateTeamType = (type) => {
              if (
                teamTypes.includes(nodeData[type]) &&
                selectedGames.includes(nodeData[`${type}_value`])
              ) {
                nodeData[type] = "";
                nodeData[`${type}_label`] = "";
                nodeData[`${type}_value`] = "";
              }
            };

            updateTeamType("team_1_of_type");
            updateTeamType("team_2_of_type");

            return { ...node, data: nodeData };
          })
      );
    }
  };

  // Function to find the root node
  const findRootNode = (nodes, edges) => {
    const nodeIdsWithParents = edges.map((edge) => edge.target);

    const hasCompassNode = nodes.some(
      (element) => element.type === "compassNode"
    );
    //** This logic is making issue if you duplicated the data and coordinates are near the actual data */
    // if (hasCompassNode) {
    //   return nodes.find((node) => !nodeIdsWithParents.includes(node.id));
    // }

    const sortedNodes = nodes.sort((a, b) => a.position.y - b.position.y);

    if (hasCompassNode) {
      return sortedNodes.find((node) => !nodeIdsWithParents.includes(node.id));
    } else {
      return sortedNodes[0];
    }
  };

  const organizeNodesParentToChild = (nodes, edges) => {
    const result = [];
    const processedNodes = new Set();
    const nodeMap = new Map(nodes.map((node) => [node.id, node]));

    const sortByMultipleCriteria = (a, b) => {
      if (a.position.x !== b.position.x) {
        return a.position.x - b.position.x;
      }

      if (a.position.y !== b.position.y) {
        return a.position.y - b.position.y;
      }

      return a.data.game_number - b.data.game_number;
    };

    const addNodeToResult = (node, level) => {
      if (!result[level]) {
        result[level] = [];
      }

      if (!processedNodes.has(node.id)) {
        result[level].push({ ...node });
        processedNodes.add(node.id);
      }

      if (node.children) {
        node.children.forEach((child) => addNodeToResult(child, level + 1));
      }
    };

    edges.forEach((edge) => {
      const sourceNode = nodeMap.get(edge.source);
      const targetNode = nodeMap.get(edge.target);

      if (sourceNode && targetNode) {
        if (!sourceNode.children) {
          sourceNode.children = [];
        }

        targetNode.level = sourceNode.level + 1;
        sourceNode.children.push(targetNode);
      }
    });

    // const sortedNodes = nodes.sort(sortByMultipleCriteria);

    const roots = nodes.filter(
      (node) => !edges.some((edge) => edge.target === node.id)
    );

    roots.forEach((root) => addNodeToResult(root, 0));

    return result;
  };

  const organizeNodesChildToParent = (nodes, edges) => {
    const result = [];
    const processedNodes = new Set();
    const nodeMap = new Map(nodes.map((node) => [node.id, node]));

    const addNodeToResult = (node, level) => {
      if (!result[level]) {
        result[level] = [];
      }

      if (!processedNodes.has(node.id)) {
        result[level].push({ ...node });
        processedNodes.add(node.id);
      }

      if (node.parent) {
        node.parent.forEach((parent) => addNodeToResult(parent, level + 1));
      }
    };

    edges.forEach((edge) => {
      const sourceNode = nodeMap.get(edge.source);
      const targetNode = nodeMap.get(edge.target);

      if (sourceNode && targetNode) {
        if (!targetNode.parent) {
          targetNode.parent = [];
        }

        targetNode.parent.push(sourceNode);
      }
    });

    const roots = nodes.filter(
      (node) => !edges.some((edge) => edge.source === node.id)
    );

    roots.forEach((root) => addNodeToResult(root, 0));

    return result;
  };

  const changeOrganizedOrder = (nodes, nodeId) => {
    const offsetIndex = nodes.findIndex((group) =>
      group.some((node) => node.id === nodeId)
    );

    if (offsetIndex !== -1) {
      const nodesAfterOffset = nodes.slice(offsetIndex);
      const nodesBeforeOffset = nodes
        .slice(0, offsetIndex)
        .map((node) => node.map((element) => ({ ...element, hasBefore: true })))
        .reverse();
      const rearrangedNodes = nodesAfterOffset.concat(nodesBeforeOffset);
      return rearrangedNodes;
    } else {
      return nodes;
    }
  };

  const organizeNodes = ({ nodes, edges }) => {
    const newData = [];

    const firstNode = findRootNode(nodes, edges);
    // const organizeData = organizeNodesParentToChild(nodes, edges);

    const organizeDataRows = organizeNodesChildToParent(nodes, edges).reverse();
    const organizeData = changeOrganizedOrder(organizeDataRows, firstNode.id);

    organizeData.forEach((item, index) => {
      if (item.length) {
        item.forEach((node, nodeIndex) => {
          const newNode = { ...node };

          if (index == 0) {
            newNode.position = {
              x:
                nodeIndex == 0
                  ? firstNode.position.x
                  : newData[nodeIndex - 1].position.x,
              y:
                nodeIndex == 0
                  ? firstNode.position.y
                  : newData[nodeIndex - 1].position.y +
                    newData[nodeIndex - 1].height +
                    50,
            };
          } else {
            let yPosition = 0;
            let xPosition = 0;
            const xOffset = 50;

            // if (newNode.hasBefore) {
            //   const childEdges = edges.find(
            //     (edge) => edge.source == newNode.id
            //   );

            //   const childNode = newData.find(
            //     (node) => node.id == childEdges.target
            //   );

            //   const positionY = childNode.height * 0.7;

            //   const yOffset =
            //     childEdges.targetHandle === "target-a" ? -positionY : positionY;
            //   yPosition = childNode.position.y + yOffset;

            //   xPosition =
            //     childNode.sourcePosition == "left"
            //       ? childNode.position.x + newNode.width + xOffset
            //       : childNode.position.x - newNode.width - xOffset;
            // } else {
            const parentsId = edges
              .filter((edge) => edge.target === node.id)
              .map((edge) => edge.source);

            if (parentsId.length) {
              const parents = newData.filter((n) => parentsId.includes(n.id));

              const sourcePosition = newNode.sourcePosition;
              xPosition =
                sourcePosition === "left"
                  ? parents[0].position.x - (parents[0].width + xOffset)
                  : parents[0].position.x + parents[0].width + xOffset;

              if (parents.length == 1) {
                if (newNode.type == "whatIfNode") {
                  xPosition = parents[0].position.x;
                  yPosition = parents[0].position.y + parents[0].height + 100;
                } else {
                  yPosition =
                    (parents[0].position.y * 2 + parents[0].height) / 2;
                }
              } else {
                yPosition =
                  parents.reduce((sum, n) => sum + n.position.y, 0) / 2;
              }
            } else {
              yPosition =
                newData[nodeIndex].position.y + newData[nodeIndex].height + 50;
              xPosition = newData[nodeIndex].position.x;
            }
            // }

            newNode.position = {
              x: xPosition,
              y: yPosition,
            };
          }

          // if (newNode.children) {
          //   delete newNode.children;
          // }

          if (newNode.parent) {
            delete newNode.parent;
          }
          if (newNode.level) {
            delete newNode.level;
          }

          if (newNode.hasBefore) {
            delete newNode.hasBefore;
          }

          newData.push(newNode);
        });
      }
    });

    return newData;
  };

  const cleanupSelected = (data) => {
    const newData = organizeNodes(data);
    const newDataLookup = Object.fromEntries(
      newData.map((item) => [item.id, item])
    );

    setNodes((nodes) =>
      nodes.map((oldNode) => {
        const newItem = newDataLookup[oldNode.id];
        return newItem ? { ...oldNode, ...newItem } : oldNode;
      })
    );
  };

  // Function expression to perform depth-first traversal to set x and y coordinates
  // const setNodeCoordinatesAutoDirection = (nodes, edges, nodeId = 0) => {
  //   const firstNode = findRootNode(nodes, edges, nodeId);
  //   const firstEdge = edges.find((edge) => edge.source == firstNode.id);

  //   if (firstEdge) {
  //     const connectEdges = edges.find(
  //       (edge) => edge.target == firstEdge.target && edge.id != firstEdge.id
  //     );

  //     let middleNode = getNode(firstEdge.target);
  //     let connectedSource = getNode(connectEdges.source);

  //     let newConnectedY = firstNode.position.y + firstNode.height + 50;
  //     let newConnectedX = firstNode.position.x;

  //     connectedSource.position.x = newConnectedX;
  //     connectedSource.position.y = newConnectedY;

  //     let newMiddleY =
  //       firstNode.position.y +
  //       (connectedSource.position.y + connectedSource.height) * 0.62;
  //     let newMiddleX = firstNode.position.x + firstNode.width + 100;

  //     middleNode.position.x = newMiddleX;
  //     middleNode.position.y = newMiddleY / 2;

  //     updateNode(connectedSource.id, { ...connectedSource });
  //     updateNode(middleNode.id, { ...middleNode });

  //     const hasExtended = edges.find((edge) => edge.source == middleNode.id);
  //     if (hasExtended) {
  //       setNodeCoordinatesAutoDirection(nodes, edges, middleNode.id);
  //     }
  //   }
  // };

  return {
    labels,
    gameLists,
    updateNode,
    updateNodeData,
    updateUsedTitles,
    updateDuplicateGames,
    deleteSingleNode,
    deleteMultipleNode,
    duplicateSingleNode,
    duplicateMultipleNode,
    updateWinner,
    updateWhatIfNode,
    deleteWhatIfNode,
    unSelectNodes,
    cleanupSelected,
    clearWinnerLoser,
  };
};

export default utils;
